import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Input from '../../components/ui/Input';
import { searchCities, type CityResult } from './api';

/**
 * CitySearchInput — debounced city autocomplete.
 *
 * Reusable wherever the user needs to pick a city (admin tools, journey
 * planner, friend management, etc.). Backed by the shared geocoder
 * (see features/geocoding/api.ts and routes/geocoding.js on the backend).
 *
 * Behavior:
 *  - Debounced 250ms
 *  - In-flight requests are aborted when the query changes
 *  - Keyboard navigation: ↑/↓ highlight, Enter selects, Esc closes
 *  - Calls onSelect with the picked CityResult; the parent decides how
 *    to display the selection afterwards.
 *  - **The results list renders in a portal on document.body** with fixed,
 *    viewport-clamped positioning anchored to the input (flipping above when
 *    there's no room below). An in-place absolute list is trapped inside any
 *    ancestor that creates a stacking context — e.g. RouteTimeline's
 *    `.rt-row { position: relative; z-index: 1 }`, where the *next* row paints
 *    over the open list no matter how high the list's own z-index is. Same
 *    reason (and same fix) as components/ui/Tooltip; see
 *    docs/codebase/ui-conventions.md.
 *  - **Self-validates on blur**: if the user typed something but didn't
 *    pick a result before tabbing out, the input shows a red border + a
 *    small inline message telling them to select from the list. The
 *    error clears as soon as they type again. This is universal across
 *    every place this component is used (the geocoded coordinates are
 *    required for downstream logic everywhere — friends map, journey
 *    planner, trip calculator), so it lives in the shared component
 *    rather than being duplicated per call site.
 */

export type CitySearchFn = (
  query: string,
  opts?: { limit?: number; signal?: AbortSignal }
) => Promise<CityResult[]>;

export interface CitySearchInputProps {
  /** Pre-filled text to show in the input (e.g. last selection's label). */
  initialLabel?: string;
  /** Placeholder when the input is empty. */
  placeholder?: string;
  /** Max results to fetch (1..10, default 8). */
  limit?: number;
  /**
   * Parent-controlled invalid flag. When true, the input renders with
   * a red border. Parents that use this typically pair it with their
   * own messaging (e.g. a toast on submit). The component *also*
   * self-detects "typed but not picked" on blur — that path is
   * independent of this prop and is what drives the inline message.
   */
  invalid?: boolean;
  /**
   * Override for the inline "select from the list" message. Default
   * wording fits the common case ("city"); use this when the input
   * is used for a non-city search surface in future.
   */
  selectionRequiredMessage?: string;
  /**
   * Called when a result is picked, or with `null` when the user edits the
   * input after picking — so a typed value never silently overrides the
   * selected city. The parent must clear its derived state on `null`.
   */
  onSelect: (city: CityResult | null) => void;
  /**
   * Optional search function override. Defaults to the auth-gated
   * `searchCities` for logged-in features (admin tools, journey planner).
   * The public Trip CO2 Calculator passes its own no-auth variant.
   */
  searchFn?: CitySearchFn;
  /** Focus the input on mount (e.g. a freshly-added destination row). */
  autoFocus?: boolean;
}

/** Gap between input and list, and the minimum breathing room to a viewport edge. */
const MENU_GAP = 4;
const MENU_VIEWPORT_MARGIN = 8;
const MENU_MAX_HEIGHT = 280;
/** Below this much room the list flips above the input rather than squeezing. */
const MENU_MIN_HEIGHT = 140;
/**
 * Above every dialog/sheet this input is used inside (FriendForm 2002,
 * CompareTransportDialog 2800, ConfirmationDialog/FormDialog 3000), below the
 * fullscreen loader (9999) and toasts (20000).
 */
const MENU_Z_INDEX = 4000;

interface MenuPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  flipUp: boolean;
}

const CitySearchInput: React.FC<CitySearchInputProps> = ({
  initialLabel,
  placeholder = 'Type a city, e.g. Lisbon',
  limit = 8,
  invalid = false,
  selectionRequiredMessage = 'Please select a city from the list.',
  onSelect,
  searchFn = searchCities,
  autoFocus = false,
}) => {
  const [query, setQuery] = useState(initialLabel ?? '');
  const [results, setResults] = useState<CityResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  // The currently-active selection. Reflects whether the input text matches
  // the last picked city's displayName right now. The parent should only
  // treat the field as filled when this is non-null.
  const [selected, setSelected] = useState<CityResult | null>(null);
  // The last city the user actually picked from the dropdown. Survives
  // typos: if the user types junk and then deletes it back to the picked
  // label, we restore `selected` from this ref. Cleared on a fresh pick
  // or an external reset via initialLabel.
  const lastPickedRef = useRef<CityResult | null>(null);

  // When we ourselves call onSelect, the parent typically echoes the change
  // back via `initialLabel`. We must skip exactly one initialLabel sync in
  // that case, otherwise the echo would clobber `selected`/`query` and
  // defeat the whole strict-selection guarantee.
  const skipNextInitialSync = useRef(false);

  /**
   * Sticky once the user has blurred the input. Combined with "has
   * typed at least 2 chars" and "no current selection", this is what
   * surfaces the inline required-selection message. Cleared as soon
   * as the user types again so the warning never sits stale while
   * they're correcting the field.
   */
  const [touched, setTouched] = useState(false);

  // Focus the input on mount when asked (e.g. a freshly-added destination row).
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the input in sync if the parent updates initialLabel (e.g. clears it).
  const lastInitial = useRef(initialLabel);
  useEffect(() => {
    if (initialLabel !== lastInitial.current) {
      lastInitial.current = initialLabel;
      if (skipNextInitialSync.current) {
        skipNextInitialSync.current = false;
        return;
      }
      setQuery(initialLabel ?? '');
      setSelected(null);
      lastPickedRef.current = null;
      // Parent did a programmatic reset; the user hasn't done anything
      // wrong yet, so clear any stale "you forgot to pick" warning.
      setTouched(false);
    }
  }, [initialLabel]);

  // Debounced fetch with AbortController
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await searchFn(trimmed, { limit, signal: controller.signal });
        setResults(list);
        setHighlighted(0);
      } catch (e: any) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        const msg = e?.response?.data?.error || e?.message || 'Search failed';
        setError(msg);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, limit, searchFn]);

  // Single chokepoint for notifying the parent. Always pairs the call with
  // a one-shot `skipNextInitialSync` flag so the parent's echo of the new
  // value (via `initialLabel`) cannot clobber our internal state.
  const notifyParent = (city: CityResult | null) => {
    skipNextInitialSync.current = true;
    onSelect(city);
  };

  const choose = (city: CityResult) => {
    setQuery(city.displayName);
    setSelected(city);
    lastPickedRef.current = city;
    setResults([]);
    setOpen(false);
    // Successful pick resolves any previous "didn't pick" state.
    setTouched(false);
    notifyParent(city);
  };

  // Decide whether the new input text resurrects, keeps, or clears the
  // current selection. Called on every keystroke.
  const reconcileSelection = (text: string) => {
    const lastPicked = lastPickedRef.current;
    if (lastPicked && text === lastPicked.displayName) {
      // Text matches the last pick's label — restore the selection
      // (e.g. user typed a stray char and then deleted it).
      if (!selected) {
        setSelected(lastPicked);
        notifyParent(lastPicked);
      }
    } else if (selected) {
      // Text diverged from the last pick — clear the selection so a typed
      // value can't masquerade as a confirmed pick.
      setSelected(null);
      notifyParent(null);
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[highlighted];
      if (pick) choose(pick);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  /**
   * Self-detected invalid: user has tabbed/clicked away after typing
   * at least 2 characters (the same threshold the search itself uses)
   * without picking a result. The 2-char minimum keeps us from
   * yelling at a user who just brushed past an empty field.
   */
  const showSelectionRequiredError =
    touched && query.trim().length >= 2 && !selected;
  const effectiveInvalid = invalid || showSelectionRequiredError;

  const showMenu = open && (loading || error || results.length > 0);

  // Anchor the portalled list to the input. Measured in a layout effect (so it
  // is positioned before paint) and re-measured on scroll/resize, which keeps
  // it glued to the input instead of closing — scrolling *inside* the list
  // leaves the anchor's rect unchanged, so that case is a no-op.
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);

  useLayoutEffect(() => {
    if (!showMenu) {
      setMenuPos(null);
      return;
    }
    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const roomBelow = window.innerHeight - rect.bottom - MENU_VIEWPORT_MARGIN;
      const roomAbove = rect.top - MENU_VIEWPORT_MARGIN;
      const flipUp = roomBelow < MENU_MIN_HEIGHT && roomAbove > roomBelow;
      setMenuPos({
        top: flipUp ? rect.top - MENU_GAP : rect.bottom + MENU_GAP,
        left: Math.max(
          MENU_VIEWPORT_MARGIN,
          Math.min(rect.left, window.innerWidth - rect.width - MENU_VIEWPORT_MARGIN),
        ),
        width: rect.width,
        maxHeight: Math.min(MENU_MAX_HEIGHT, Math.max(MENU_MIN_HEIGHT, flipUp ? roomAbove : roomBelow)),
        flipUp,
      });
    };
    measure();
    let frame = 0;
    const onViewportChange = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };
    // Capture phase so scrolls in any ancestor container are caught too.
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [showMenu, results.length, loading, error]);

  return (
    <div ref={anchorRef} style={{ position: 'relative' }}>
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        error={effectiveInvalid}
        aria-invalid={effectiveInvalid || undefined}
        value={query}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          setOpen(true);
          reconcileSelection(next);
          // Typing = "I'm correcting it" → clear the warning so it
          // doesn't sit stale on screen while the user is mid-edit.
          if (touched) setTouched(false);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so a click on a list item still registers.
          window.setTimeout(() => setOpen(false), 150);
          // Once the user has left the field, the "should have picked"
          // check is fair game on every future render.
          setTouched(true);
        }}
        onKeyDown={onKeyDown}
      />
      {showSelectionRequiredError && (
        <div
          role="alert"
          style={{
            marginTop: 4,
            fontSize: 12,
            color: '#b91c1c',
            lineHeight: 1.4,
          }}
        >
          {selectionRequiredMessage}
        </div>
      )}
      {showMenu && menuPos && createPortal(
        <div
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            // Flipped: anchor the box's BOTTOM edge at the input's top.
            transform: menuPos.flipUp ? 'translateY(-100%)' : undefined,
            zIndex: MENU_Z_INDEX,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
            maxHeight: menuPos.maxHeight,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div style={{ padding: 10, fontSize: 13, color: '#64748b' }}>Searching…</div>
          )}
          {error && !loading && (
            <div style={{ padding: 10, fontSize: 13, color: '#b91c1c' }}>{error}</div>
          )}
          {!loading && !error &&
            results.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(c)}
                onMouseEnter={() => setHighlighted(i)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  fontSize: 14,
                  background: i === highlighted ? '#f1f5f9' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{c.name}</span>
                  {c.kind === 'country' && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: '#0e5c40',
                        background: '#e7f1ec',
                        borderRadius: 999,
                        padding: '2px 8px',
                        flexShrink: 0,
                      }}
                    >
                      Country
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {c.kind === 'country'
                    ? 'Anywhere in the country'
                    : [c.region, c.country].filter(Boolean).join(', ')}
                </div>
              </button>
            ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default CitySearchInput;
